import React, { useState } from "react";
import Swal from "sweetalert2";
import "./PatientHistorySearch.css";
import "./PatientHistorySearchDateSearch.css";
import MedicalDocumentManager from "./MedicalDocumentManager";
import PatientProcedureHistory from "./PatientProcedureHistory";
import "./Procedure.css";
import { API_BASE } from "../apiConfig";

async function apiRequest(url, options = {}) {
  let response;
  try {
    response = await fetch(url, options);
  } catch {
    throw new Error("Unable to connect to server.");
  }

  const text = await response.text();
  if (!text.trim()) {
    console.error("Empty API response:", { url, status: response.status });
    throw new Error(
      `Server returned an empty response. HTTP ${response.status}. Check the PHP API/server error log.`
    );
  }

  let result;
  try {
    result = JSON.parse(text);
  } catch {
    console.error("Invalid JSON response:", text);
    throw new Error("Server returned invalid JSON.");
  }

  if (!response.ok || result.status === "error") {
    throw new Error(result.msg || result.message || `Server error ${response.status}`);
  }
  return result;
}

const emptyPatientForm = {
  id: "",
  mr_no: "",
  name: "",
  phone: "",
  age: "",
  sex: "",
  remarks: ""
};

const emptyMedicalForm = {
  bp: "",
  sugar: "",
  height: "",
  weight: "",
  medical_history: ""
};

export default function PatientHistorySearch() {
  const [searchText, setSearchText] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [searchMode, setSearchMode] = useState("patient");
  const [patients, setPatients] = useState([]);
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [history, setHistory] = useState(null);
  const [historyDetails, setHistoryDetails] = useState([]);
  const [visits, setVisits] = useState([]);
  const [attachments, setAttachments] = useState([]);

  const [loadingSearch, setLoadingSearch] = useState(false);
  const [loadingPatient, setLoadingPatient] = useState(false);
  const [searched, setSearched] = useState(false);
  const [expandedVisit, setExpandedVisit] = useState(null);
  const [visitPrescriptionData, setVisitPrescriptionData] = useState(null);

  const [editPatientOpen, setEditPatientOpen] = useState(false);
  const [patientForm, setPatientForm] = useState(emptyPatientForm);
  const [savingPatient, setSavingPatient] = useState(false);

  const [editMedicalOpen, setEditMedicalOpen] = useState(false);
  const [medicalForm, setMedicalForm] = useState(emptyMedicalForm);
  const [savingMedical, setSavingMedical] = useState(false);

  const [uploadFiles, setUploadFiles] = useState([]);
  const [uploadVisitId, setUploadVisitId] = useState("0");
  const [uploading, setUploading] = useState(false);

  const searchPatient = async () => {
    const keyword = searchText.trim();

    if (!keyword) {
      Swal.fire({
        icon: "warning",
        title: "Enter Search",
        text: "Enter MR Number, Phone Number or Patient Name."
      });
      return;
    }

    try {
      setLoadingSearch(true);
      setSearched(true);
      setSearchMode("patient");
      setPatients([]);
      setSelectedPatient(null);
      setHistory(null);
      setHistoryDetails([]);
      setVisits([]);
      setAttachments([]);
      setVisitPrescriptionData(null);

      const result = await apiRequest(
        `${API_BASE}search_patient_history.php?q=${encodeURIComponent(keyword)}`
      );

      setPatients(Array.isArray(result.data) ? result.data : []);
    } catch (error) {
      Swal.fire({ icon: "error", title: "Search Error", text: error.message });
    } finally {
      setLoadingSearch(false);
    }
  };

  const searchAppointmentsByDate = async () => {
    if (!dateFrom || !dateTo) {
      Swal.fire("Select Dates", "Select both From and To dates.", "warning");
      return;
    }
    if (dateFrom > dateTo) {
      Swal.fire("Invalid Dates", "From date must not be after To date.", "warning");
      return;
    }
    try {
      setLoadingSearch(true);
      setSearched(true);
      setSearchMode("dates");
      setPatients([]);
      setSelectedPatient(null);
      setHistory(null);
      setHistoryDetails([]);
      setVisits([]);
      setAttachments([]);
      setExpandedVisit(null);
      setVisitPrescriptionData(null);
      const result = await apiRequest(
        `${API_BASE}search_appointment_dates.php?from=${encodeURIComponent(dateFrom)}&to=${encodeURIComponent(dateTo)}`
      );
      setPatients(Array.isArray(result.data) ? result.data : []);
    } catch (error) {
      Swal.fire("Appointment Search Failed", error.message, "error");
    } finally {
      setLoadingSearch(false);
    }
  };

  const loadPatient = async (patientId, fallback = null) => {
    const result = await apiRequest(
      `${API_BASE}get_patient_full_history.php?patient_id=${encodeURIComponent(patientId)}`
    );

    setSelectedPatient(result.patient || fallback);
    setHistory(
      result.history || { bp: "", sugar: "", height: "", weight: "", medical_history: "" }
    );
    setHistoryDetails(Array.isArray(result.history_details) ? result.history_details : []);
    setVisits(Array.isArray(result.visits) ? result.visits : []);
    setAttachments(Array.isArray(result.attachments) ? result.attachments : []);
    return result;
  };

  const viewPatient = async (patient) => {
    try {
      setLoadingPatient(true);
      setSelectedPatient(patient);
      setHistory(null);
      setHistoryDetails([]);
      setVisits([]);
      setAttachments([]);
      setExpandedVisit(null);
      setVisitPrescriptionData(null);
      await loadPatient(patient.id, patient);
    } catch (error) {
      Swal.fire({ icon: "error", title: "Unable to Load Patient", text: error.message });
    } finally {
      setLoadingPatient(false);
    }
  };

  const refreshSelectedPatient = async () => {
    if (!selectedPatient?.id) return;
    try {
      setLoadingPatient(true);
      await loadPatient(selectedPatient.id, selectedPatient);
    } catch (error) {
      Swal.fire({ icon: "error", title: "Refresh Failed", text: error.message });
    } finally {
      setLoadingPatient(false);
    }
  };

  const clearSearch = () => {
    setSearchText("");
    setDateFrom("");
    setDateTo("");
    setSearchMode("patient");
    setPatients([]);
    setSelectedPatient(null);
    setHistory(null);
    setHistoryDetails([]);
    setVisits([]);
    setAttachments([]);
    setSearched(false);
    setExpandedVisit(null);
    setVisitPrescriptionData(null);
  };

  const openPatientEdit = () => {
    if (!selectedPatient) return;
    setPatientForm({
      id: selectedPatient.id || "",
      mr_no: selectedPatient.mr_no || "",
      name: selectedPatient.name || "",
      phone: selectedPatient.phone || "",
      age: selectedPatient.age || "",
      sex: selectedPatient.sex || "",
      remarks: selectedPatient.remarks || ""
    });
    setEditPatientOpen(true);
  };

  const savePatient = async (e) => {
    e.preventDefault();

    if (!patientForm.name.trim()) {
      Swal.fire("Validation", "Patient name is required.", "warning");
      return;
    }
    if (patientForm.phone && !/^\d{10}$/.test(patientForm.phone)) {
      Swal.fire("Validation", "Phone number must contain 10 digits.", "warning");
      return;
    }

    try {
      setSavingPatient(true);
      await apiRequest(`${API_BASE}update_patient.php`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(patientForm)
      });
      setEditPatientOpen(false);
      await refreshSelectedPatient();

      setPatients((prev) =>
        prev.map((p) =>
          Number(p.id) === Number(patientForm.id) ? { ...p, ...patientForm } : p
        )
      );

      Swal.fire("Updated", "Patient information updated successfully.", "success");
    } catch (error) {
      Swal.fire("Update Failed", error.message, "error");
    } finally {
      setSavingPatient(false);
    }
  };

  const openMedicalEdit = () => {
    setMedicalForm({
      bp: history?.bp || "",
      sugar: history?.sugar || "",
      height: history?.height || "",
      weight: history?.weight || "",
      medical_history:
        history?.medical_history ||
        historyDetails.map((x) => (typeof x === "string" ? x : x.detail)).filter(Boolean).join("\n")
    });
    setEditMedicalOpen(true);
  };

  const saveMedicalHistory = async (e) => {
    e.preventDefault();
    if (!selectedPatient?.id) return;

    try {
      setSavingMedical(true);
      await apiRequest(`${API_BASE}update_patient_medical_history.php`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          patient_id: selectedPatient.id,
          ...medicalForm
        })
      });
      setEditMedicalOpen(false);
      await refreshSelectedPatient();
      Swal.fire("Updated", "Medical history updated successfully.", "success");
    } catch (error) {
      Swal.fire("Update Failed", error.message, "error");
    } finally {
      setSavingMedical(false);
    }
  };

  const uploadAttachments = async () => {
    if (!selectedPatient?.id) return;
    if (!uploadFiles.length) {
      Swal.fire("Select Files", "Choose one or more medical documents.", "warning");
      return;
    }

    const allowed = ["doc","docx","xls","xlsx","pdf","jpg","jpeg","png","webp"];
    for (const file of uploadFiles) {
      const ext = file.name.split(".").pop().toLowerCase();
      if (!allowed.includes(ext)) {
        Swal.fire("Invalid File", `${file.name} is not supported.`, "warning");
        return;
      }
      if (file.size > 10 * 1024 * 1024) {
        Swal.fire("File Too Large", `${file.name} exceeds 10 MB.`, "warning");
        return;
      }
    }

    const formData = new FormData();
    formData.append("patient_id", selectedPatient.id);
    formData.append("visit_id", uploadVisitId || "0");
    formData.append("appointment_id", "");
    uploadFiles.forEach(file => formData.append("files[]", file));

    try {
      setUploading(true);
      await apiRequest(`${API_BASE}upload_medical_documents.php`, { method: "POST", body: formData });
      setUploadFiles([]);
      const input = document.getElementById("patient-report-files");
      if (input) input.value = "";
      await refreshSelectedPatient();
      Swal.fire("Uploaded", "Medical document(s) uploaded successfully.", "success");
    } catch (error) {
      Swal.fire("Upload Failed", error.message, "error");
    } finally {
      setUploading(false);
    }
  };


  const deleteAttachment = async (attachment) => {
    const confirm = await Swal.fire({
      icon: "warning",
      title: "Remove attachment?",
      text: attachment.original_name || "This medical report will be permanently removed.",
      showCancelButton: true,
      confirmButtonText: "Yes, remove",
      confirmButtonColor: "#d33"
    });

    if (!confirm.isConfirmed) return;

    try {
      await apiRequest(`${API_BASE}delete_medical_document.php`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          document_id: attachment.id,
          patient_id: selectedPatient.id
        })
      });
      setAttachments((prev) =>
        prev.filter((item) => Number(item.id) !== Number(attachment.id))
      );
      Swal.fire("Removed", "Attachment removed successfully.", "success");
    } catch (error) {
      Swal.fire("Delete Failed", error.message, "error");
    }
  };

  const openVisitPrescription = async (visit) => {
    if (!visit?.id) return;

    try {
      Swal.fire({
        title: "Loading Prescription...",
        text: "Please wait",
        allowOutsideClick: false,
        didOpen: () => Swal.showLoading()
      });

      const result = await apiRequest(
        `${API_BASE}get_visit_details.php?visit_id=${encodeURIComponent(visit.id)}`
      );

      Swal.close();

      const data = result.data || {};

      setVisitPrescriptionData({
        ...data,
        mr_no: data.mr_no || selectedPatient?.mr_no || "",
        name: data.name || selectedPatient?.name || "",
        phone: data.phone || selectedPatient?.phone || "",
        age: data.age || selectedPatient?.age || "",
        sex: data.sex || selectedPatient?.sex || ""
      });
    } catch (error) {
      Swal.close();
      Swal.fire({
        icon: "error",
        title: "Unable to Load Prescription",
        text: error.message
      });
    }
  };


  const renderPrescriptionContent = (value) => {
    const raw = String(value || "").trim();

    if (!raw) {
      return "";
    }

    const medicineRows = [];
    const notes = [];

    raw
      .split(/\r?\n/)
      .map((line) => line.trim())
      .filter(Boolean)
      .forEach((line) => {
        const parts = line
          .split(":")
          .map((part) => part.trim());

        if (parts.length >= 3) {
          medicineRows.push({
            medicine: parts[0] || "-",
            duration: parts[1] || "-",
            dosage: parts.slice(2).join(":").trim() || "-"
          });
        } else {
          notes.push(line);
        }
      });

    let html = "";

    if (medicineRows.length > 0) {
      html += `
        <div class="medicine-table-wrap">
          <table class="medicine-table">
            <thead>
              <tr>
                <th class="medicine-col">Medicine</th>
                <th class="duration-col">Duration</th>
                <th class="dosage-col">Dosage</th>
              </tr>
            </thead>
            <tbody>
              ${medicineRows
                .map(
                  (row) => `
                    <tr>
                      <td class="medicine-name">${escapeHtml(row.medicine)}</td>
                      <td>${escapeHtml(row.duration)}</td>
                      <td class="dosage-text">${escapeHtml(row.dosage)}</td>
                    </tr>
                  `
                )
                .join("")}
            </tbody>
          </table>
        </div>
      `;
    }

    if (notes.length > 0) {
      html += `
        <div class="prescription-notes">
          ${notes
            .map(
              (note) => `
                <div class="prescription-note">
                  ${escapeHtml(note)}
                </div>
              `
            )
            .join("")}
        </div>
      `;
    }

    return html;
  };


  const printVisitPrescription = (pageSelection = "all") => {
    if (!visitPrescriptionData) return;

    const patient = visitPrescriptionData;
    const pages = [];

    const mainPrescription = String(patient.prescription || "").trim();
    if (mainPrescription) {
      pages.push({
        key: "main",
        title: "Prescription",
        content: mainPrescription
      });
    }

    const extraPages = Array.isArray(patient.additional_prescriptions)
      ? patient.additional_prescriptions
      : [];

    extraPages.forEach((item, index) => {
      const content = String(item.content || "").trim();
      if (!content) return;

      pages.push({
        key: `extra:${item.id || index}`,
        title:
          String(item.title || "").trim() ||
          `Additional Prescription ${index + 1}`,
        content
      });
    });

    const pagesToPrint =
      pageSelection === "all"
        ? pages
        : pages.filter((page) => page.key === pageSelection);

    if (!pagesToPrint.length) {
      Swal.fire({
        icon: "info",
        title: "Nothing to Print",
        text: "No printable prescription page was found for this visit."
      });
      return;
    }

    const printWindow = window.open("", "_blank", "width=800,height=900");

    if (!printWindow) {
      Swal.fire({
        icon: "warning",
        title: "Popup Blocked",
        text: "Please allow popups in your browser to print the prescription."
      });
      return;
    }

    const sheets = pagesToPrint
      .map(
        (page) => `
          <section class="a5-page">
            <div class="prescription-header">
              <h1>Kolath Medical Centre</h1>
              <p>${escapeHtml(page.title)}</p>
            </div>

            <div class="patient-grid">
              <div><strong>MR ID:</strong> ${escapeHtml(patient.mr_no || "-")}</div>
              <div><strong>Date:</strong> ${escapeHtml(formatDateTime(patient.visit_date))}</div>
              <div><strong>Name:</strong> ${escapeHtml(patient.name || "-")}</div>
              <div><strong>Phone:</strong> ${escapeHtml(patient.phone || "-")}</div>
              <div><strong>Age:</strong> ${escapeHtml(patient.age || "-")}</div>
              <div><strong>Gender:</strong> ${escapeHtml(patient.sex || "-")}</div>
            </div>

            <div class="page-title">${escapeHtml(page.title)}</div>
            <div class="prescription-content">${renderPrescriptionContent(page.content)}</div>

            <div class="doctor-signature">Doctor Signature</div>
          </section>
        `
      )
      .join("");

    const html = `
<!doctype html>
<html>
<head>
<meta charset="utf-8">
<title>Prescription - ${escapeHtml(patient.mr_no || "")}</title>
<style>
  @page { size: A5 portrait; margin: 10mm; }
  * { box-sizing: border-box; }
  html, body {
    margin: 0;
    padding: 0;
    background: #fff;
    color: #111;
    font-family: Arial, Helvetica, sans-serif;
  }
  .a5-page {
    width: 128mm;
    min-height: 190mm;
    margin: 0 auto;
    padding: 2mm 3mm;
  }
  .a5-page:not(:last-child) {
    page-break-after: always;
    break-after: page;
  }
  .prescription-header {
    text-align: center;
    border-bottom: 2px solid #222;
    padding-bottom: 8px;
    margin-bottom: 10px;
  }
  .prescription-header h1 { margin: 0; font-size: 20px; }
  .prescription-header p {
    margin: 4px 0 0;
    font-size: 12px;
    font-weight: 700;
  }
  .patient-grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 5px 14px;
    font-size: 12px;
    border-bottom: 1px solid #ccc;
    padding-bottom: 9px;
    margin-bottom: 12px;
  }
  .page-title {
    margin: 12px 0 14px;
    padding-bottom: 6px;
    text-align: center;
    font-size: 15px;
    font-weight: 700;
    letter-spacing: 0.5px;
    text-transform: uppercase;
    border-bottom: 1px solid #bbb;
  }

  .prescription-content {
    min-height: 100mm;
    font-size: 13px;
    line-height: 1.55;
  }

  .medicine-table-wrap {
    width: 100%;
    margin-top: 4px;
  }

  .medicine-table {
    width: 100%;
    border-collapse: collapse;
    table-layout: fixed;
    border: none;
  }

  .medicine-table th,
  .medicine-table td {
    border: none !important;
    padding: 6px 5px;
    text-align: left;
    vertical-align: top;
    word-break: break-word;
  }

  .medicine-table thead th {
    padding-top: 2px;
    padding-bottom: 7px;
    font-size: 11px;
    font-weight: 700;
    text-transform: uppercase;
    color: #444;
  }

  .medicine-table tbody td {
    font-size: 13px;
    line-height: 1.45;
  }

  .medicine-col {
    width: 52%;
  }

  .duration-col {
    width: 22%;
  }

  .dosage-col {
    width: 26%;
  }

  .medicine-name {
    font-weight: 600;
    padding-left: 0 !important;
  }

  .dosage-text {
    white-space: nowrap;
    font-weight: 600;
    letter-spacing: 0.2px;
  }

  .prescription-notes {
    margin-top: 14px;
    padding-top: 8px;
    border-top: 1px solid #ddd;
  }

  .prescription-note {
    margin: 4px 0;
    font-size: 12.5px;
    line-height: 1.5;
  }
  .doctor-signature {
    margin-top: 15mm;
    text-align: right;
    font-size: 11px;
  }
  @media print {
    .a5-page { width: auto; min-height: auto; margin: 0; }
  }
</style>
</head>
<body>
${sheets}
<script>
  window.onafterprint = function () {
    setTimeout(function () {
      window.close();
    }, 100);
  };

  window.onload = function () {
    setTimeout(function () {
      window.focus();
      window.print();
    }, 250);
  };
<\/script>
</body>
</html>`;

    printWindow.document.open();
    printWindow.document.write(html);
    printWindow.document.close();
  };

  const deactivatePatient = async () => {
    if (!selectedPatient?.id) return;

    const confirm = await Swal.fire({
      icon: "warning",
      title: "Deactivate patient?",
      html: `<b>${escapeHtml(selectedPatient.name || "")}</b><br/>MR: ${escapeHtml(
        selectedPatient.mr_no || ""
      )}<br/><br/>The patient will no longer appear in active patient searches.`,
      showCancelButton: true,
      confirmButtonText: "Deactivate",
      confirmButtonColor: "#d33"
    });

    if (!confirm.isConfirmed) return;

    try {
      await apiRequest(`${API_BASE}deactivate_patient.php`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ patient_id: selectedPatient.id })
      });

      setPatients((prev) =>
        prev.filter((p) => Number(p.id) !== Number(selectedPatient.id))
      );
      setSelectedPatient(null);
      setHistory(null);
      setHistoryDetails([]);
      setVisits([]);
      setAttachments([]);
      Swal.fire("Deactivated", "Patient deactivated successfully.", "success");
    } catch (error) {
      Swal.fire("Deactivation Failed", error.message, "error");
    }
  };

  return (
    <div className="patient-history-page">
      {/* <div className="ph-header">
        <div>
          <h2><i className="fa fa-search"></i>&nbsp; Patient History Search</h2>
          <p>Search patients and view or update complete consultation history.</p>
        </div>
      </div> */}

      <div className="ph-search-card">
        <div className="ph-search-panels">
          <section className="ph-search-panel ph-patient-search-panel">
            <div className="ph-search-title"><i className="fa fa-user-md"></i> Find Patient</div>
            <div className="ph-search-row">
              <div className="ph-search-input-wrapper">
                <i className="fa fa-search"></i>
                <input
                  type="text"
                  value={searchText}
                  onChange={(e) => setSearchText(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && searchPatient()}
                  placeholder="Enter MR Number, Phone Number or Patient Name"
                  autoComplete="off"
                />
              </div>
              <button type="button" className="ph-search-button" onClick={searchPatient} disabled={loadingSearch}>
                <i className={loadingSearch ? "fa fa-spinner fa-spin" : "fa fa-search"}></i>
                {loadingSearch ? " Searching..." : " Search"}
              </button>
              <button type="button" className="ph-clear-button" onClick={clearSearch}>
                <i className="fa fa-refresh"></i> Clear
              </button>
            </div>
            <div className="ph-search-help">
              <span><i className="fa fa-id-card-o"></i> MR Number</span>
              <span><i className="fa fa-phone"></i> Phone Number</span>
              <span><i className="fa fa-user"></i> Patient Name</span>
            </div>
          </section>
          <section className="ph-search-panel ph-date-search-panel">
            <div className="ph-search-title"><i className="fa fa-calendar"></i> Search by Appointment Date</div>
            <div className="ph-date-fields">
              <label>From Date
                <input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} />
              </label>
              <label>To Date
                <input type="date" value={dateTo} min={dateFrom || undefined} onChange={(e) => setDateTo(e.target.value)} />
              </label>
            </div>
            <button type="button" className="ph-search-button ph-date-search-button" onClick={searchAppointmentsByDate} disabled={loadingSearch}>
              <i className={loadingSearch ? "fa fa-spinner fa-spin" : "fa fa-calendar-check-o"}></i>
              {loadingSearch ? " Searching..." : " Search Appointments"}
            </button>
          </section>
        </div>
      </div>

      <div className="ph-main-layout">
        <div className="ph-results-column">
          <div className="ph-section-header" style={{color:'#005657',borderBottom:'1px solid silver'}}>
            <div><i className="fa fa-users"></i> &nbsp; {searchMode === "dates" ? "Patients by Appointment Date" : "Search Results"}</div>
            <span>{patients.length}</span>
          </div>

          {loadingSearch ? (
            <div className="ph-loading"><i className="fa fa-spinner fa-spin"></i> Searching patients...</div>
          ) : patients.length ? (
            <div className="ph-patient-list">
              {patients.map((patient) => (
                <div
                  key={patient.id}
                  className={
                    selectedPatient && Number(selectedPatient.id) === Number(patient.id)
                      ? "ph-patient-item active"
                      : "ph-patient-item"
                  }
                  onClick={() => viewPatient(patient)}
                >
                  <div className="ph-avatar"><i className="fa fa-user"></i></div>
                  <div className="ph-patient-summary">
                    <strong>{patient.name || "-"}</strong>
                    <span>MR: {patient.mr_no || "-"}</span>
                    <small><i className="fa fa-phone"></i> {patient.phone || "-"}</small>
                    {searchMode === "dates" && (
                      <small><i className="fa fa-calendar"></i> {formatDateTime(patient.last_appointment_date)}
                        {Number(patient.appointment_count) > 1 ? ` (${patient.appointment_count} appointments)` : ""}
                      </small>
                    )}
                  </div>
                  <button type="button" className="ph-view-button">
                    <i className="fa fa-chevron-right"></i>
                  </button>
                </div>
              ))}
            </div>
          ) : searched ? (
            <Empty icon="fa fa-user-times" title="No Patient Found"
              text={searchMode === "dates" ? "No appointments found in the selected date range." : "Try another MR Number, phone number or patient name."} />
          ) : (
            <Empty icon="fa fa-search" title="Search Patient"
              text="Patient search results will appear here." />
          )}
        </div>

        <div className="ph-details-column">
          {loadingPatient ? (
            <div className="ph-loading ph-big-loading">
              <i className="fa fa-spinner fa-spin"></i> Loading patient history...
            </div>
          ) : selectedPatient ? (
            <>
              <section className="ph-card">
                <div className="ph-card-heading" style={{color:'#005657',borderBottom:'1px solid silver'}}>
                  <div><i className="fa fa-address-card-o"></i> Patient Information</div>
                  <div className="ph-action-buttons">
                    <button className="ph-btn ph-btn-edit" onClick={openPatientEdit}>
                      <i className="fa fa-pencil"></i> Edit
                    </button>
                    <button className="ph-btn ph-btn-danger" onClick={deactivatePatient}>
                      <i className="fa fa-ban"></i> Deactivate
                    </button>
                  </div>
                </div>

                <div className="ph-profile">
                  <div className="ph-profile-avatar"><i className="fa fa-user"></i></div>
                  <div>
                    <h3>{selectedPatient.name || "-"}</h3>
                    <span>MR Number: <strong>{selectedPatient.mr_no || "-"}</strong></span>
                  </div>
                </div>

                <div className="ph-info-grid">
                  <InfoBox icon="fa fa-id-card-o" label="MR Number" value={selectedPatient.mr_no} />
                  <InfoBox icon="fa fa-user" label="Patient Name" value={selectedPatient.name} />
                  <InfoBox icon="fa fa-phone" label="Phone Number" value={selectedPatient.phone} />
                  <InfoBox icon="fa fa-calendar" label="Age"
                    value={selectedPatient.age ? `${selectedPatient.age} Years` : "-"} />
                  <InfoBox icon="fa fa-venus-mars" label="Gender" value={selectedPatient.sex} />
                  <InfoBox icon="fa fa-calendar-check-o" label="Registration Date"
                    value={formatDate(selectedPatient.date)} />
                </div>

                <div className="ph-address">
                  <label><i className="fa fa-map-marker"></i> Address / Remarks</label>
                  <p>{selectedPatient.remarks || "-"}</p>
                </div>
              </section>

              <section className="ph-card">
                <div className="ph-card-heading" style={{color:'#005657',borderBottom:'1px solid silver'}}>
                  <div><i className="fa fa-heartbeat"></i> Patient Medical History</div>
                  <button className="ph-btn ph-btn-edit" onClick={openMedicalEdit}>
                    <i className="fa fa-pencil"></i> Edit Medical History
                  </button>
                </div>

                <div className="ph-vitals-grid">
                  <VitalCard icon="fa fa-heartbeat" label="Blood Pressure" value={history?.bp} />
                  <VitalCard icon="fa fa-tint" label="Sugar" value={history?.sugar} />
                  <VitalCard icon="fa fa-arrows-v" label="Height" value={history?.height} unit="cm" />
                  <VitalCard icon="fa fa-balance-scale" label="Weight" value={history?.weight} unit="kg" />
                </div>

                {historyDetails.length > 0 && (
                  <div className="ph-other-history">
                    <h4><i className="fa fa-file-text-o"></i> Other Difficulties / Medical History</h4>
                    {historyDetails.map((item, index) => (
                      <div className="ph-history-detail" key={item.id || index}>
                        <span>{index + 1}</span>
                        <p>{item.detail || item}</p>
                      </div>
                    ))}
                  </div>
                )}
              </section>

              <section className="ph-card">
                <PatientProcedureHistory
                  patientId={selectedPatient.id}
                  heading="Procedure History / Pending Procedures"
                  canEdit={String(localStorage.getItem("userRole") || "").trim().toLowerCase() === "doctor"}
                  canComplete={String(localStorage.getItem("userRole") || "").trim().toLowerCase() === "doctor"}
                />
              </section>

              <section className="ph-card">
                <div className="ph-card-heading" style={{color:'#005657',borderBottom:'1px solid silver'}}>
                  <div><i className="fa fa-paperclip"></i> &nbsp; Medical Reports / Attachments</div>
                </div>
                <div className="ph-upload-box">
                  <select className="ph-upload-visit-select" value={uploadVisitId} onChange={(e) => setUploadVisitId(e.target.value)}>
                    <option value="0">General Patient Document</option>
                    {visits.map((visit) => (
                      <option key={visit.id} value={visit.id}>Visit: {formatDateTime(visit.visit_date)}</option>
                    ))}
                  </select>
                </div>
                <MedicalDocumentManager patientId={selectedPatient.id} visitId={uploadVisitId} heading="Medical Reports / Attachments" />
              </section>

              <section className="ph-card">
                <div className="ph-card-heading" style={{color:'#005657',borderBottom:'1px solid silver'}}>
                  <div><i className="fa fa-history"></i> &nbsp; Visit History</div>
                  <span className="ph-visit-count">{visits.length} Visit{visits.length === 1 ? "" : "s"}</span>
                </div>

                {visits.length ? (
                  <div className="ph-visits">
                    {visits.map((visit, index) => {
                      const open = expandedVisit === visit.id;
                      return (
                        <div className={open ? "ph-visit open" : "ph-visit"} key={visit.id}>
                          <div className="ph-visit-header"
                            onClick={() => setExpandedVisit(open ? null : visit.id)}>
                            <div className="ph-visit-number">{visits.length - index}</div>
                            <div className="ph-visit-title">
                              <strong>{formatDateTime(visit.visit_date)}</strong>
                              <span>{visit.purpose
                                ? truncateText(visit.purpose, 70)
                                : visit.prescription
                                  ? truncateText(visit.prescription, 70)
                                : visit.doctor_notes
                                  ? truncateText(visit.doctor_notes, 70)
                                  : "Consultation"}</span>
                            </div>
                            <div className="ph-visit-status">
                              <span>{visit.visit_status || "completed"}</span>
                              <i className={open ? "fa fa-chevron-up" : "fa fa-chevron-down"}></i>
                            </div>
                          </div>
                          {open && (
                            <div className="ph-visit-body">
                              <VisitField label="Purpose of Visit" icon="fa fa-clipboard" value={visit.purpose} />
                              <div className="ph-visit-vitals">
                                <MiniInfo label="BP" value={visit.bp} />
                                <MiniInfo label="Sugar" value={visit.sugar} />
                                <MiniInfo label="Height" value={visit.height ? `${visit.height} cm` : "-"} />
                                <MiniInfo label="Weight" value={visit.weight ? `${visit.weight} kg` : "-"} />
                                <MiniInfo label="Temperature" value={visit.temperature != null && String(visit.temperature).trim() !== "" ? `${String(visit.temperature).trim()}${/[°]|\b(?:F|C|fahrenheit|celsius)\b/i.test(String(visit.temperature)) ? "" : " °F"}` : "-"} />
                              </div>
                              <VisitField label="Doctor Notes" icon="fa fa-user-md" value={visit.doctor_notes} />
                              <VisitField label="Prescription" icon="fa fa-medkit" value={visit.prescription} />
                              <VisitField label="Tests / Check-up" icon="fa fa-flask" value={visit.tests} />
                              <VisitField label="Injection (Legacy)" icon="fa fa-eyedropper" value={visit.injections} />
                              <VisitField label="Remarks" icon="fa fa-comment-o" value={visit.remarks} />

                              <div style={{ marginTop: "14px", textAlign: "right" }}>
                                <button
                                  type="button"
                                  className="ph-btn ph-btn-primary"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    openVisitPrescription(visit);
                                  }}
                                >
                                  <i className="fa fa-print"></i> Prescription / Print A5
                                </button>
                              </div>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div className="ph-empty-history">
                    <i className="fa fa-history"></i>
                    <h4>No Previous Visits</h4>
                    <p>Consultation history will appear here.</p>
                  </div>
                )}
              </section>
            </>
          ) : (
            <div className="ph-select-patient">
              <div><i className="fa fa-user-md"></i></div>
              <h3>Select a Patient</h3>
              <p>Search and select a patient to view complete medical and visit history.</p>
            </div>
          )}
        </div>
      </div>

      {editPatientOpen && (
        <Modal title="Edit Patient Information" onClose={() => setEditPatientOpen(false)}>
          <form onSubmit={savePatient} className="ph-modal-form">
            <div className="ph-form-grid">
              <Field label="MR Number" value={patientForm.mr_no} disabled />
              <Field label="Patient Name *" value={patientForm.name}
                onChange={(v) => setPatientForm({ ...patientForm, name: v })} />
              <Field label="Phone Number" value={patientForm.phone} maxLength={10}
                onChange={(v) => setPatientForm({ ...patientForm, phone: v.replace(/\D/g, "") })} />
              <Field label="Age" type="number" value={patientForm.age}
                onChange={(v) => setPatientForm({ ...patientForm, age: v })} />
              <div className="ph-field">
                <label>Gender</label>
                <select value={patientForm.sex}
                  onChange={(e) => setPatientForm({ ...patientForm, sex: e.target.value })}>
                  <option value="">Select</option>
                  <option value="Male">Male</option>
                  <option value="Female">Female</option>
                  <option value="Other">Other</option>
                </select>
              </div>
              <div className="ph-field ph-field-full">
                <label>Address / Remarks</label>
                <textarea rows="4" value={patientForm.remarks}
                  onChange={(e) => setPatientForm({ ...patientForm, remarks: e.target.value })} />
              </div>
            </div>
            <ModalActions busy={savingPatient} onCancel={() => setEditPatientOpen(false)}
              text="Update Patient" />
          </form>
        </Modal>
      )}

      {editMedicalOpen && (
        <Modal title="Edit Medical History" onClose={() => setEditMedicalOpen(false)}>
          <form onSubmit={saveMedicalHistory} className="ph-modal-form">
            <div className="ph-form-grid">
              <Field label="Blood Pressure" value={medicalForm.bp}
                onChange={(v) => setMedicalForm({ ...medicalForm, bp: v })} placeholder="120/80" />
              <Field label="Sugar" value={medicalForm.sugar}
                onChange={(v) => setMedicalForm({ ...medicalForm, sugar: v })} />
              <Field label="Height (cm)" type="number" value={medicalForm.height}
                onChange={(v) => setMedicalForm({ ...medicalForm, height: v })} />
              <Field label="Weight (kg)" type="number" step="0.01" value={medicalForm.weight}
                onChange={(v) => setMedicalForm({ ...medicalForm, weight: v })} />
              <div className="ph-field ph-field-full">
                <label>Other Difficulties / Medical History</label>
                <textarea
                  rows="7"
                  value={medicalForm.medical_history}
                  onChange={(e) => setMedicalForm({ ...medicalForm, medical_history: e.target.value })}
                  placeholder="Enter one item per line"
                />
                <small>Each non-empty line is saved as a separate medical-history detail.</small>
              </div>
            </div>
            <ModalActions busy={savingMedical} onCancel={() => setEditMedicalOpen(false)}
              text="Update Medical History" />
          </form>
        </Modal>
      )}

      {visitPrescriptionData && (
        <VisitPrescriptionModal
          data={visitPrescriptionData}
          onClose={() => setVisitPrescriptionData(null)}
          onPrint={printVisitPrescription}
        />
      )}
    </div>
  );
}

function VisitPrescriptionModal({ data, onClose, onPrint }) {
  const extraPages = Array.isArray(data.additional_prescriptions)
    ? data.additional_prescriptions
    : [];

  const hasMainPrescription = Boolean(String(data.prescription || "").trim());
  const printableExtras = extraPages.filter((item) => String(item.content || "").trim());
  const printableCount = (hasMainPrescription ? 1 : 0) + printableExtras.length;

  return (
    <div className="ph-modal-overlay" onMouseDown={onClose}>
      <div
        className="ph-modal"
        onMouseDown={(e) => e.stopPropagation()}
        style={{ maxWidth: "850px", width: "94vw" }}
      >
        <div className="ph-modal-header">
          <div>
            <h3><i className="fa fa-file-text-o"></i> Previous Visit Prescription</h3>
            <small>
              {data.name || "-"} · MR {data.mr_no || "-"} · {formatDateTime(data.visit_date)}
            </small>
          </div>
          <button type="button" onClick={onClose}><i className="fa fa-times"></i></button>
        </div>

        <div style={{ padding: "18px", maxHeight: "70vh", overflowY: "auto" }}>
          <div className="ph-info-grid" style={{ marginBottom: "14px" }}>
            <InfoBox icon="fa fa-user" label="Patient" value={data.name} />
            <InfoBox icon="fa fa-id-card-o" label="MR Number" value={data.mr_no} />
            <InfoBox icon="fa fa-phone" label="Phone" value={data.phone} />
            <InfoBox icon="fa fa-calendar" label="Visit Date" value={formatDateTime(data.visit_date)} />
          </div>

          <VisitField label="Doctor Notes" icon="fa fa-user-md" value={data.doctor_notes} />
          <VisitField label="Prescription" icon="fa fa-medkit" value={data.prescription} />

          {hasMainPrescription && (
            <div style={{ marginTop: "8px", textAlign: "right" }}>
              <button type="button" className="ph-btn ph-btn-primary" onClick={() => onPrint("main")}>
                <i className="fa fa-print"></i> Print Main Prescription A5
              </button>
            </div>
          )}

          <VisitField label="Tests / Check-up" icon="fa fa-flask" value={data.tests} />

          {extraPages.length > 0 && (
            <div style={{ marginTop: "18px" }}>
              <h4 style={{ margin: "0 0 8px" }}>
                Separate A5 Test / Referral Prescriptions
              </h4>

              {extraPages.map((item, index) => {
                const key = `extra:${item.id || index}`;
                const content = String(item.content || "").trim();

                return (
                  <div
                    key={item.id || `${item.title}-${index}`}
                    style={{
                      marginTop: "10px",
                      padding: "12px",
                      border: "1px solid #d7dee8",
                      borderRadius: "10px",
                      background: "#f8fbff"
                    }}
                  >
                    <div style={{ display: "flex", justifyContent: "space-between", gap: "12px", flexWrap: "wrap" }}>
                      <div style={{ flex: "1 1 320px" }}>
                        <strong>{item.title || `Additional Prescription ${index + 1}`}</strong>
                        <div style={{ marginTop: "7px", whiteSpace: "pre-wrap", lineHeight: 1.5 }}>
                          {item.content || "-"}
                        </div>
                      </div>

                      {content && (
                        <button type="button" className="ph-btn ph-btn-primary" onClick={() => onPrint(key)}>
                          <i className="fa fa-print"></i> Print This A5
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          <VisitField label="Injection" icon="fa fa-eyedropper" value={data.injections} />
          <VisitField label="Remarks" icon="fa fa-comment-o" value={data.remarks} />
        </div>

        <div className="ph-modal-actions" style={{ padding: "0 18px 18px" }}>
          <button type="button" className="ph-btn ph-btn-light" onClick={onClose}>Close</button>
          {printableCount > 0 && (
            <button type="button" className="ph-btn ph-btn-primary" onClick={() => onPrint("all")}>
              <i className="fa fa-print"></i>{" "}
              {printableCount > 1
                ? `Print All ${printableCount} A5 Pages`
                : "Print A5 Prescription"}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function Modal({ title, onClose, children }) {
  return (
    <div className="ph-modal-overlay" onMouseDown={onClose}>
      <div className="ph-modal" onMouseDown={(e) => e.stopPropagation()}>
        <div className="ph-modal-header">
          <h3>{title}</h3>
          <button type="button" onClick={onClose}><i className="fa fa-times"></i></button>
        </div>
        {children}
      </div>
    </div>
  );
}

function ModalActions({ busy, onCancel, text }) {
  return (
    <div className="ph-modal-actions">
      <button type="button" className="ph-btn ph-btn-light" onClick={onCancel}>Cancel</button>
      <button type="submit" className="ph-btn ph-btn-primary" disabled={busy}>
        <i className={busy ? "fa fa-spinner fa-spin" : "fa fa-save"}></i>
        {busy ? " Saving..." : ` ${text}`}
      </button>
    </div>
  );
}

function Field({ label, value, onChange, type = "text", disabled = false, ...rest }) {
  return (
    <div className="ph-field">
      <label>{label}</label>
      <input
        type={type}
        value={value ?? ""}
        disabled={disabled}
        onChange={(e) => onChange && onChange(e.target.value)}
        {...rest}
      />
    </div>
  );
}

function Empty({ icon, title, text }) {
  return <div className="ph-empty"><i className={icon}></i><h4>{title}</h4><p>{text}</p></div>;
}

function InfoBox({ icon, label, value }) {
  return (
    <div className="ph-info-box">
      <div className="ph-info-icon"><i className={icon}></i></div>
      <div><small>{label}</small><strong>{value || "-"}</strong></div>
    </div>
  );
}

function VitalCard({ icon, label, value, unit = "" }) {
  return (
    <div className="ph-vital-card">
      <div className="ph-vital-icon"><i className={icon}></i></div>
      <div><span>{label}</span><strong>{value || "-"}{value && unit ? ` ${unit}` : ""}</strong></div>
    </div>
  );
}

function MiniInfo({ label, value }) {
  return <div className="ph-mini-info"><small>{label}</small><strong>{value || "-"}</strong></div>;
}

function VisitField({ label, icon, value }) {
  return (
    <div className="ph-visit-field">
      <div className="ph-visit-field-title"><i className={icon}></i> {label}</div>
      <div className="ph-visit-field-value">{value || "-"}</div>
    </div>
  );
}

function formatDate(value) {
  if (!value) return "-";
  const d = new Date(String(value).replace(" ", "T"));
  return Number.isNaN(d.getTime())
    ? value
    : d.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
}

function formatDateTime(value) {
  if (!value) return "-";
  const d = new Date(String(value).replace(" ", "T"));
  return Number.isNaN(d.getTime())
    ? value
    : d.toLocaleString("en-IN", {
        day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit"
      });
}

function truncateText(value, length) {
  if (!value) return "";
  const text = String(value);
  return text.length <= length ? text : `${text.substring(0, length)}...`;
}

function formatBytes(bytes) {
  const n = Number(bytes || 0);
  if (!n) return "0 KB";
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

function isPdf(file) {
  return (file.mime_type || "").toLowerCase().includes("pdf") ||
    (file.original_name || "").toLowerCase().endsWith(".pdf");
}

function escapeHtml(value) {
  return String(value).replace(/[&<>"']/g, (c) => ({
    "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#039;"
  }[c]));
}
